/**
 * Inventories API Mutations
 *
 * Create/update operations.
 */

import { apiClient } from '../client';
import type { InventoryShowResponse, UpdateInventoryPayload, CreateInventoryPayload } from './types';

function buildFormData(payload: CreateInventoryPayload | UpdateInventoryPayload, prefix = 'inventory'): FormData {
  const formData = new FormData();

  for (const [key, value] of Object.entries(payload)) {
    if (key === 'images') {
      (value as File[]).forEach((file) => {
        formData.append(`${prefix}[images][]`, file);
      });
    } else if (key === 'remove_image_ids') {
      (value as string[]).forEach((id) => {
        formData.append(`${prefix}[remove_image_ids][]`, id);
      });
    } else if (key === 'inventory_configurations_attributes') {
      (value as Record<string, unknown>[]).forEach((config, index) => {
        for (const [configKey, configValue] of Object.entries(config)) {
          formData.append(
            `${prefix}[inventory_configurations_attributes][${index}][${configKey}]`,
            configValue == null ? '' : String(configValue),
          );
        }
      });
    } else if (value === null) {
      // Explicit null = "clear this field" — send empty string so Rails
      // applies the clear instead of the multipart path silently dropping
      // the key (which would leave the previous value untouched).
      formData.append(`${prefix}[${key}]`, '');
    } else if (value !== undefined) {
      formData.append(`${prefix}[${key}]`, String(value));
    }
  }

  return formData;
}

function hasFiles(payload: CreateInventoryPayload | UpdateInventoryPayload): boolean {
  return (
    ('images' in payload && Array.isArray(payload.images) && payload.images.length > 0) ||
    ('remove_image_ids' in payload &&
      Array.isArray(payload.remove_image_ids) &&
      payload.remove_image_ids.length > 0)
  );
}

const MULTIPART_CONFIG = { headers: { 'Content-Type': 'multipart/form-data' } };

export async function createInventory(payload: CreateInventoryPayload): Promise<InventoryShowResponse> {
  if (hasFiles(payload)) {
    const formData = buildFormData(payload);
    const { data } = await apiClient.post<InventoryShowResponse>('/api/inventories', formData, MULTIPART_CONFIG);
    return data;
  }
  const { data } = await apiClient.post<InventoryShowResponse>('/api/inventories', { inventory: payload });
  return data;
}

export async function updateInventory(
  id: number,
  payload: UpdateInventoryPayload,
): Promise<InventoryShowResponse> {
  if (hasFiles(payload)) {
    const formData = buildFormData(payload);
    const { data } = await apiClient.patch<InventoryShowResponse>(`/api/inventories/${id}`, formData, MULTIPART_CONFIG);
    return data;
  }
  const { data } = await apiClient.patch<InventoryShowResponse>(`/api/inventories/${id}`, {
    inventory: payload,
  });
  return data;
}
